import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Upload, Trash2, Loader2, RefreshCw } from 'lucide-react';
import bridgeApi from './bridgeApi';

/**
 * ImageUploader — Uploads to WP media library via bridge proxy.
 * Supports: upload, preview, replace, remove.
 * Props:
 *   orgId          - org ID for proxy routing
 *   currentUrl     - current image URL (for preview)
 *   entityType     - 'team' | 'player' | 'venue' | 'sport' | 'league'
 *   entityId       - WP post/term ID of the entity
 *   field          - meta field name (e.g. 'team_logo', 'player_photo')
 *   onUploaded     - callback({url, attachment_id}) after successful upload
 *   onRemoved      - callback() after image removed
 *   label          - display label (default 'Image')
 */
export default function ImageUploader({ orgId, currentUrl, entityType, entityId, field, onUploaded, onRemoved, label = 'Image', uploadMode = 'bridge', category, owner }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError('File too large (max 10MB)');
      return;
    }
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setError('File type not supported. Use JPG, PNG, WebP, or GIF.');
      return;
    }

    setUploading(true);
    setError(null);

    // Preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    try {
      // Classified mode (team logos): upload to the GridFS media SOT via
      // /api/wp-media/upload-classified and store the root-relative OL file URL
      // (/api/wp-media/file/{org}/{aid}). WP mirror happens server-side on save.
      if (uploadMode === 'classified') {
        const data = await bridgeApi.mediaUploadClassified(orgId, file, category || 'logo', { owner }, file.name);
        if (data.ok) {
          const aid = data.data?.attachment_id;
          onUploaded?.({ url: `/api/wp-media/file/${orgId}/${aid}`, attachment_id: aid });
          setPreview(null);
        } else {
          setError(data.error?.message || 'Upload failed');
          setPreview(null);
        }
        setUploading(false);
        return;
      }
      // Convert to base64
      const base64 = await fileToBase64(file);
      const body = {
        filename: file.name,
        content_type: file.type,
        data_base64: base64,
      };
      // If entity exists, attach directly
      if (entityId && field) {
        body.attach_to = { entity_type: entityType, entity_id: entityId, field };
      }

      const data = await bridgeApi.upload(orgId, body);
      if (data.ok) {
        onUploaded?.({ url: data.data.url, attachment_id: data.data.attachment_id, ...data.data });
        setPreview(null);
      } else {
        setError(data.error?.message || 'Upload failed');
        setPreview(null);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setPreview(null);
    }
    setUploading(false);
  };

  const handleRemove = async () => {
    if (!window.confirm(`Remove this ${label.toLowerCase()}?`)) return;
    onRemoved?.();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const displayUrl = preview || currentUrl;
  // Root-relative OL URLs (/api/wp-media/file/...) need the backend origin to
  // render in the DEV UI; empty REACT_APP_BACKEND_URL (VPS build) keeps them
  // same-origin. Data URLs (fresh preview) are left untouched.
  const toAbs = (u) => (u && u.startsWith('/')) ? `${process.env.REACT_APP_BACKEND_URL || ''}${u}` : u;

  return (
    <div className="space-y-2" data-testid={`image-uploader-${field || 'generic'}`}>
      {displayUrl ? (
        <Card className="bg-slate-800/40 border-slate-700/50 overflow-hidden">
          <div className="relative group">
            <img
              src={toAbs(displayUrl)}
              alt={label}
              className="w-full h-32 object-contain bg-slate-900 p-2"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            {uploading && (
              <div className="absolute inset-0 bg-slate-900/80 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
              </div>
            )}
            <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button variant="secondary" size="icon" className="h-7 w-7 bg-slate-800/90" onClick={() => fileRef.current?.click()} data-testid="replace-image-btn">
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
              <Button variant="secondary" size="icon" className="h-7 w-7 bg-red-900/90 hover:bg-red-800" onClick={handleRemove} data-testid="remove-image-btn">
                <Trash2 className="h-3.5 w-3.5 text-red-300" />
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${dragOver ? 'border-amber-500 bg-amber-500/10' : 'border-slate-700 hover:border-slate-600'}`}
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          data-testid="image-drop-zone"
        >
          {uploading ? (
            <Loader2 className="h-6 w-6 animate-spin text-amber-500 mx-auto" />
          ) : (
            <>
              <Upload className="h-6 w-6 text-slate-500 mx-auto mb-1" />
              <p className="text-xs text-slate-400">Drop image or click to upload</p>
              <p className="text-[10px] text-slate-600 mt-0.5">JPG, PNG, WebP, GIF — max 10MB</p>
            </>
          )}
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      // Strip data:...;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
